import Button from '@mui/material/Button';
import { useState } from "react";
import { Box, FormControl, FormGroup, Typography } from "@mui/material";
import { ArrowBack, CheckBox, CheckBoxOutlineBlank, CheckCircle, Close } from "@mui/icons-material";
import { ProfileResponse } from "@/utils/Hooks";
import { ProfileData, toImproveLabel } from "@/types/shared.types";
import { KeyedMutator } from "swr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import color from "@/style/theme/Color";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { faFaceAngry, faFaceFrown, faFaceMeh, faFaceSmile, faGrinBeam } from "@fortawesome/free-solid-svg-icons";
import { displayDate } from "@/utils/dateDisplay";

const ExitModalPages = [
  {
    title: "Rating",
    modal: RatingPage
  },
  {
    title: "Bagian",
    modal: ToImprovePage
  },
  {
    title: "Akhir",
    modal: ReviewPage
  },
  {
    title: "Selesai",
    modal: SelesaiPage,
    noReturn: true,
    excludeOnStages: true
  }
];

type KunjunganData = {
  lockerNumber: string;
}

type ExitModalProps = {
  accessToken: string,
  setOpen: (open: boolean) => void,
  profile?: { data: ProfileData | undefined; mutate: KeyedMutator<ProfileResponse> | undefined } | undefined

  visitorToken: string,
  handleRootBack: () => void;
}

export default function ExitModal({ accessToken, setOpen, profile, visitorToken, handleRootBack }: ExitModalProps) {
  const [page, setPage] = useState(0);
  const handleClose = () => setOpen(false);

  const [rating, setRating] = useState(0);
  const [toImprove, setToImprove] = useState({
    operasional: false,
    kenyamanan: false,
    koleksi: false,
    simpus: false,
    fasilitas: false,
    informasi: false,
    pelayanan: false
  });

  const goToNextPage = () => {
    if (page === ExitModalPages.length - 1) {
      handleClose();
      return;
    }
    setPage(page + 1);
  };

  const goToPrevPage = () => {
    if (page === 0) {
      handleRootBack();
      return;
    }
    setPage(page - 1);
  };

  return <>
    <Box sx={
      {
        display: 'flex',
        flexDirection: 'row',
        gap: 1,
        marginBottom: 1
      }
    }>
      {ExitModalPages.map(({ title, modal, excludeOnStages }, index) => {
        if (excludeOnStages) {
          return null;
        }
        return <Box
          sx={{ flexGrow: 1 }}
          key={index}
        >
          <Box
            sx={{
              borderBottom: "2px solid",
              borderBottomColor: index <= page ? "primary.main" : "rgba(0, 0, 0, 0.20)"
            }}
          ></Box>
          <Typography>{title}</Typography>
        </Box>;
      })}
      <Button
        sx={{
          flexShrink: 1,
          minWidth: 0,
          padding: 0
        }}
        onClick={handleClose}>
        <Close/>
      </Button>
    </Box>

    {(!ExitModalPages[page].noReturn) && <Button
      variant="outlined"
      onClick={goToPrevPage}
      size="small"
      startIcon={<ArrowBack/>}
      sx={{
        marginBottom: 1
      }}
    >
      Kembali
    </Button>
    }
    {ExitModalPages.map(({ title, modal }, index) => {
      if (index !== page) {
        return null;
      }
      const Page = modal;
      return <Page
        key={title}
        accessToken={accessToken}
        visitorToken={visitorToken}
        profile={profile}
        nextCallback={goToNextPage}
        prevCallback={goToPrevPage}
        rating={rating}
        setRating={setRating}
        toImprove={toImprove}
        setToImprove={setToImprove}
      />;
    })}
  </>;
}

type ExitModalPageProps = {
  key?: any;

  nextCallback: () => void;
  prevCallback: () => void;
  accessToken: string;
  profile?: { data: ProfileData | undefined; mutate: KeyedMutator<ProfileResponse> | undefined } | undefined

  visitorToken: string;

  rating: number;
  setRating: (rating: number) => void;

  toImprove: object;
  setToImprove: (toImprove: object) => void;
}

function RatingPage({ nextCallback, rating, setRating, key }: ExitModalPageProps) {
  const messageForm = '';
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down('sm'));

  const [hoveredRating, setHoveredRating] = useState(0);

  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }}
              key={key}>
    <Typography component="h2" sx={{
      fontSize: 20,
      fontWeight: 700
    }}>Bagaimana pengalaman Anda dengan kami?</Typography>

    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <FormControl fullWidth={true}>
        <FormGroup
          row={true}
          sx={{ marginLeft: 'auto', marginRight: 'auto' }}
        >
          <div style={{ textAlign: 'center' }}
               onClick={() => setRating(1)}
               onMouseEnter={() => setHoveredRating(1)}
               onMouseLeave={() => setHoveredRating(0)}>
            <FontAwesomeIcon
              // icon="face-angry"
              icon={faFaceAngry}
              size={sm ? '4x' : '3x'}
              style={{
                margin: '4px',
                color: hoveredRating >= 1 ? color.yellow : rating >= 1 ? color.ut_orange : color.grey
              }}
            />
          </div>
          <div style={{ textAlign: 'center' }}
               onClick={() => setRating(2)}
               onMouseEnter={() => setHoveredRating(2)}
               onMouseLeave={() => setHoveredRating(0)}>
            <FontAwesomeIcon
              // icon="face-frown"
              icon={faFaceFrown}
              size={sm ? '4x' : '3x'}
              style={{
                margin: '4px',
                color: hoveredRating >= 2 ? color.yellow : rating >= 2 ? color.ut_orange : color.grey
              }}
            />
          </div>
          <div style={{ textAlign: 'center' }}
               onClick={() => setRating(3)}
               onMouseEnter={() => setHoveredRating(3)}
               onMouseLeave={() => setHoveredRating(0)}>
            <FontAwesomeIcon
              // icon="face-meh"
              icon={faFaceMeh}
              size={sm ? '4x' : '3x'}
              style={{
                margin: '4px',
                color: hoveredRating >= 3 ? color.yellow : rating >= 3 ? color.ut_orange : color.grey
              }}
            />
          </div>
          <div style={{ textAlign: 'center' }}
               onClick={() => setRating(4)}
               onMouseEnter={() => setHoveredRating(4)}
               onMouseLeave={() => setHoveredRating(0)}>
            <FontAwesomeIcon
              // icon="face-smile"
              icon={faFaceSmile}
              size={sm ? '4x' : '3x'}
              style={{
                margin: '4px',
                color: hoveredRating >= 4 ? color.yellow : rating >= 4 ? color.ut_orange : color.grey
              }}
            />
          </div>
          <div style={{ textAlign: 'center' }}
               onClick={() => setRating(5)}
               onMouseEnter={() => setHoveredRating(5)}
               onMouseLeave={() => setHoveredRating(0)}>
            <FontAwesomeIcon
              // icon="face-grin-beam"
              icon={faGrinBeam}
              size={sm ? '4x' : '3x'}
              style={{
                margin: '4px',
                color: hoveredRating >= 5 ? color.yellow : rating >= 5 ? color.ut_orange : color.grey
              }}
            />
          </div>
        </FormGroup>
      </FormControl>
    </Box>
    <Button
      variant="contained"
      onClick={nextCallback}
      sx={{
        width: '100%'
      }}
      size="large"
      disabled={rating === 0}
    >
      Berikutnya
    </Button>
  </Box>;
}

function ToImprovePage({ nextCallback, toImprove, setToImprove, key }: ExitModalPageProps) {
  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }}
              key={key}>
    <Typography component="h2" sx={{
      fontSize: 20,
      fontWeight: 700
    }}>Apa saja yang perlu kami tingkatkan?</Typography>

    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      {Object.keys(toImproveLabel).map((key, index) => {
        return <Button
          key={index}
          variant={toImprove[key] ? "contained" : "outlined"}
          onClick={() => setToImprove({ ...toImprove, [key]: !toImprove[key] })}
          sx={{
            width: '100%',
            textAlign: 'left'
          }}
          size="large"
          startIcon={toImprove[key] ? <CheckBox/> : <CheckBoxOutlineBlank/>}
        >
          <Typography sx={{
            marginRight: 'auto',
            marginLeft: 'auto'
          }}>{toImproveLabel[key]}</Typography>
        </Button>;
      })}
    </Box>
    <Button
      variant="contained"
      onClick={nextCallback}
      sx={{
        width: '100%'
      }}
      size="large"
    >
      Berikutnya
    </Button>
  </Box>;
}

function ReviewPage(
  {
    nextCallback,
    accessToken,
    visitorToken,
    profile,
    rating,
    toImprove,
    key
  }: ExitModalPageProps) {
  const [loading, setLoading] = useState(false);

  const submitVisitor = async () => {
    setLoading(true);

    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/v2/visitor/exit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        token: visitorToken,
        rating: rating,
        komentar: Object.keys(toImprove).filter(key => toImprove[key]).join(', ')
      })
    });

    setLoading(false);

    if (!response.ok) {
      return;
    }

    if (profile && profile.data && profile.mutate) profile.mutate({
      data: {
        ...profile.data,
        kunjungan: null
      }
    });

    nextCallback();

  };

  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }}
              key={key}>
    <Typography component="h2" sx={{
      fontSize: 20,
      fontWeight: 700
    }}>Review kembali</Typography>

    <Typography>
      Anda berkunjung dengan rincian berikut.
    </Typography>

    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Typography component="h3" sx={{
        fontWeight: 700
      }}>Tanggal</Typography>
      <Typography>{displayDate(profile?.data?.kunjungan?.waktuMasuk)}</Typography>
    </Box>

    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Typography component="h3" sx={{
        fontWeight: 700
      }}>Peminjaman loker</Typography>
      <Typography>{profile?.data?.kunjungan?.loker ? 'Ya' : 'Tidak'} {profile?.data?.kunjungan?.loker && `(${profile?.data?.kunjungan?.loker})`}</Typography>

      <Typography component="h3" sx={{
        fontWeight: 700
      }}>Peminjaman tas</Typography>
      <Typography>{profile?.data?.kunjungan?.tas ? 'Ya' : 'Tidak'} {profile?.data?.kunjungan?.tas && `(${profile?.data?.kunjungan?.tas})`}</Typography>
    </Box>

    <Button
      variant="contained"
      onClick={submitVisitor}
      disabled={loading}
      sx={{
        width: '100%'
      }}
      size="large"
    >
      Keluar
    </Button>
  </Box>;
}

function SelesaiPage({ nextCallback, profile, key }: ExitModalPageProps) {

  // read from profile once, may change so save it
  const [loker] = useState(!!profile?.data?.kunjungan?.loker);
  // profile?.mutate();

  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }}
              key={key}>
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      alignItems: 'center'
    }}>
      <CheckCircle sx={{
        width: 96,
        height: 96
      }}/>

      <Typography component="h2" sx={{
        fontSize: 20,
        fontWeight: 700
      }}>Kunjungan diakhiri</Typography>
    </Box>
    <Typography>
      <b>Kunjungan Anda telah diakhiri.</b> Jangan lupa untuk membawa barang-barang Anda sebelum meninggalkan kawasan
      perpustakaan.
    </Typography>

    {loker && <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>
      <Typography component="h3" sx={{
        fontWeight: 700
      }}>Peminjaman loker</Typography>
      <Typography>Anda meminjam loker. <b>Jangan lupa untuk mengembalikan kunci loker</b> yang Anda pinjam sebelumnya
        sebelum
        meninggalkan kawasan perpustakaan.</Typography>
    </Box>
    }

    <Button
      variant="contained"
      onClick={nextCallback}
      sx={{
        width: '100%'
      }}
      size="large"
    >
      Selesai
    </Button>
  </Box>;
}